import { useRef, useState } from 'react';
import { S } from '../../domain/state.js';
import { fMoney, todayISO, wDY } from '../../lib/format.js';
import { DEXP, DINC, cat } from '../../domain/categories.js';
import { recFreqLabel } from '../../domain/recurring.js';
import { balToday } from '../../domain/balance.js';
import { confirmDialog } from '../../components/ui.jsx';
import { UI, useApp, ls, toast, setDark } from '../../store/ui.js';
import { Icon } from '../../components/Icon.jsx';
import { useMedia } from '../../lib/useMedia.js';
import { saveBalance, saveMonthStart, addCategory, deleteCategory, logout } from '../../store/actions.js';
import { getConfig, clearConfig } from '../../lib/supabase.js';
import { openRecModal } from './RecModal.jsx';
import { openClassify } from '../dashboard/Classify.jsx';
import { exportBackup, importBackup } from './backup.js';
import { InstallSetting } from '../../components/Install.jsx';

const GROUPS = [
  ['bal', 'יתרת פתיחה', 'wallet', () => <OpeningBalance />],
  ['rec', 'חיובים קבועים', 'calendar', () => <RecList />],
  ['cats', 'קטגוריות', 'list', () => <Categories />],
  ['backup', 'גיבוי ושחזור', 'download', () => <Backup />],
  ['general', 'כללי', 'settings', () => <General />],
  ['account', 'חשבון', 'logout', () => <Account />],
];

export function SettingsPage() {
  useApp();
  const desktop = useMedia('(min-width: 1024px)');
  const [open, setOpenState] = useState(() => ls.get('set_open', ''));
  const pick = (k) => { ls.set('set_open', k); setOpenState(k); };
  if (desktop) {
    const cur = GROUPS.find((g) => g[0] === open) || GROUPS[0];
    return (
      <div className="set-layout">
        <nav className="set-nav">
          {GROUPS.map(([k, label, ico]) => (
            <button key={k} className={'nav-item' + (cur[0] === k ? ' on' : '')} onClick={() => pick(k)}><Icon name={ico} size={18} />{label}</button>
          ))}
        </nav>
        <div>
          <div className="sec" style={{ fontSize: '1.15rem' }}>{cur[1]}</div>
          {cur[3]()}
        </div>
      </div>
    );
  }
  return (
    <div className="period" style={{ padding: '4px 16px' }}>
      {GROUPS.map(([k, label, ico, render]) => (
        <div key={k}>
          <div className="acc-h" onClick={() => pick(open === k ? '' : k)} role="button" tabIndex={0}>
            <span className="row"><Icon name={ico} size={18} style={{ color: 'var(--ink3)' }} />{label}</span>
            <Icon name="chevD" size={18} style={{ color: 'var(--ink3)', transform: open === k ? 'rotate(180deg)' : undefined }} />
          </div>
          {open === k && <div style={{ paddingBottom: 14 }}>{render()}</div>}
        </div>
      ))}
    </div>
  );
}

function OpeningBalance() {
  const [f, setF] = useState({ amount: S.set.balDate ? S.set.balAmount : '', date: S.set.balDate || '', eod: S.set.balEod ? '1' : '' });
  const b = balToday();
  async function save() {
    const n = parseFloat(String(f.amount).replace(/[^0-9.-]/g, ''));
    if (isNaN(n)) return toast('הזן סכום');
    if (!f.date) return toast('בחר תאריך');
    if (f.date > todayISO()) return toast('התאריך לא יכול להיות בעתיד');
    await saveBalance(n, f.date, f.eod === '1');
    toast('✅ יתרת פתיחה: ' + fMoney(n) + ' נכון ל' + (f.eod ? 'סוף' : 'תחילת') + ' יום ' + wDY(f.date));
  }
  return (
    <div className="ss">
      <div className="note mb12">נקודת ההתחלה של כל החישובים: היתרה בעו"ש בתאריך מסוים. היתרה היום מחושבת ממנה, ועוד כל תנועות העו"ש שנסרקו אחרי התאריך הזה.</div>
      <div className="sr"><div><div className="slbl">💰 סכום</div><div className="ssub">במינוס: עם סימן מינוס, למשל -6985</div></div>
        <input type="number" inputMode="decimal" dir="ltr" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} style={{ width: 120, textAlign: 'center' }} /></div>
      <div className="sr"><div className="slbl">📅 נכון לתאריך</div><input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} style={{ width: 'auto' }} /></div>
      <div className="sr"><div><div className="slbl">🕐 נכון ל</div><div className="ssub">האם התנועות של אותו יום כבר בתוך הסכום</div></div>
        <select value={f.eod} onChange={(e) => setF({ ...f, eod: e.target.value })} style={{ width: 'auto' }}><option value="">תחילת היום</option><option value="1">סוף היום</option></select></div>
      <button className="ubtn ubtn-primary w100 mt10" onClick={save}>שמור יתרת פתיחה</button>
      <div className="sub mt10" style={{ fontSize: '.86rem' }}>
        {!S.set.balDate ? 'עוד לא הוגדרה יתרת פתיחה, ולכן אין תחזית.' : <>
          לפי החישוב, היתרה היום: <b>{fMoney(b.amount)}</b> = {fMoney(b.base)}{b.count ? ' ועוד ' + b.count + ' תנועות עו"ש שנסרקו מאז (' + fMoney(b.delta, b.delta >= 0 ? '+' : '') + ')' : ', אין תנועות עו"ש סרוקות מאז'}.
          <br /><span className="muted">אם זה לא מה שהבנק מראה, חסרות תנועות שלא נסרקו. אפשר פשוט להזין כאן את היתרה של היום, נכון לסוף היום.</span>
        </>}
      </div>
    </div>
  );
}

function RecList() {
  const list = S.rec.slice().sort((a, b) => (a.type !== b.type ? (a.type === 'income' ? -1 : 1) : (Number(a.day_of_month) || 1) - (Number(b.day_of_month) || 1)));
  return (
    <div className="ss">
      <div className="note mb8">משמשים את תחזית התזרים בלבד. הם אינם נרשמים כפעולות, כי העסקה האמיתית מגיעה ממילא בסריקת דף הבנק או האשראי. לחיצה על חיוב פותחת עריכה.</div>
      {!list.length && <div className="note">אין חיובים קבועים</div>}
      {list.map((r) => {
        const c = cat(r.type, r.cat), src = r.type === 'income' ? '' : (r.source || 'cash') === 'credit' ? '💳 אשראי' : '🏦 עו"ש';
        return (
          <div className="ri clickable" key={r.id} onClick={() => openRecModal(r.id)}>
            <div className={'txic ' + r.type} style={{ width: 32, height: 32, fontSize: '.9rem', borderRadius: 8 }}>{c.icon}</div>
            <div className="ri-info"><div className="ri-name">{r.description}</div><div className="ri-meta">יום {r.day_of_month} בחודש{recFreqLabel(r) ? ' · ' + recFreqLabel(r) : ''}{src ? ' · ' + src : ''}</div></div>
            <div className={'ri-amt ' + r.type}>{fMoney(r.amount, r.type === 'income' ? '+' : '−')}</div>
            <span style={{ color: 'var(--ink4)', fontSize: '.9rem', padding: '0 4px' }}>✎</span>
          </div>
        );
      })}
      <button className="ubtn ubtn-primary ubtn-sm w100 mt12" onClick={() => openRecModal()}>➕ הוסף חיוב קבוע</button>
    </div>
  );
}

function Categories() {
  const [type, setType] = useState('expense');
  const [f, setF] = useState({ label: '', icon: '📦', color: '#c8603a' });
  const defs = type === 'income' ? DINC : DEXP, custom = S.cats.filter((c) => c.type === type);
  async function add() {
    if (!f.label.trim()) return toast('נא להזין שם');
    try { await addCategory({ ...f, label: f.label.trim(), type }); setF({ label: '', icon: '📦', color: f.color }); toast('✅ קטגוריה נוספה'); } catch (e) { toast('⚠️ ' + e.message); }
  }
  async function del(c) {
    if (!(await confirmDialog({ title: 'מחיקת קטגוריה', body: 'למחוק את "' + c.label + '"? פעולות שמסווגות בה יוצגו כ"אחר".', okLabel: 'מחק', danger: true }))) return;
    try { await deleteCategory(c.id); toast('🗑️ נמחק'); } catch (e) { toast('⚠️ ' + e.message); }
  }
  return (
    <div className="ss">
      <button className="ubtn ubtn-outline w100 mb12" onClick={openClassify}>🏷️ סיווג עסקים שב"אחר"</button>
      <div className="ctabs">
        <button className={'ctab' + (type === 'expense' ? ' on' : '')} onClick={() => setType('expense')}>הוצאות</button>
        <button className={'ctab' + (type === 'income' ? ' on' : '')} onClick={() => setType('income')}>הכנסות</button>
      </div>
      {defs.map((c) => (
        <div className="ci" key={c.id}><div className="cico" style={{ background: c.color + '22' }}>{c.icon}</div>
          <div className="cinfo"><div className="cname">{c.label}</div><div className="ctype">ברירת מחדל</div></div>
          <span className="note" style={{ padding: '2px 7px', borderRadius: 99, background: 'var(--surface2)' }}>קבוע</span></div>
      ))}
      {custom.map((c) => (
        <div className="ci" key={c.id}><div className="cico" style={{ background: (c.color || '#888') + '22' }}>{c.icon}</div>
          <div className="cinfo"><div className="cname">{c.label}</div><div className="ctype">מותאם</div></div>
          <button className="del-btn" onClick={() => del(c)} aria-label="מחק">🗑️</button></div>
      ))}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 4 }}>
        <div className="sub mb8" style={{ fontWeight: 700 }}>הוסף קטגוריה</div>
        <div className="form-row">
          <div><label className="lbl">שם קטגוריה</label><input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="למשל: ניקיון" /></div>
          <div><label className="lbl">צבע</label><input type="color" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} style={{ height: 46, padding: 2, cursor: 'pointer' }} /></div>
        </div>
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <div><label className="lbl">אמוג'י</label><input value={f.icon} onChange={(e) => setF({ ...f, icon: e.target.value })} style={{ fontSize: '1.4rem', textAlign: 'center', width: 80 }} /></div>
          <button className="ubtn ubtn-primary" style={{ flex: 1 }} onClick={add}>➕ הוסף קטגוריה</button>
        </div>
      </div>
    </div>
  );
}

function Backup() {
  const ref = useRef(null);
  return (
    <div className="ss">
      <div className="sub mb12">גיבוי מלא של כל הנתונים לקובץ JSON. שמור במקום בטוח.</div>
      <div className="row" style={{ flexWrap: 'wrap' }}>
        <button className="ubtn ubtn-primary ubtn-sm" style={{ flex: 1, minWidth: 140 }} onClick={exportBackup}>⬇️ ייצא הכל</button>
        <button className="ubtn ubtn-outline ubtn-sm" style={{ flex: 1, minWidth: 140 }} onClick={() => ref.current.click()}>⬆️ ייבא גיבוי</button>
        <input ref={ref} type="file" accept=".json" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files[0]; e.target.value = ''; if (f) importBackup(f); }} />
      </div>
      <div className="note mt6">💡 ייבוא מוסיף רק מה שחסר. הוא לא מוחק ולא דורס נתונים קיימים.</div>
    </div>
  );
}

function General() {
  const [ms, setMs] = useState(S.set.monthStart);
  async function saveMs() {
    const d = parseInt(ms, 10);
    if (d === S.set.monthStart) return;
    const r = await saveMonthStart(d);
    if (r === null && (d < 1 || d > 28 || !d)) { setMs(S.set.monthStart); return; }
    if (!r) { toast('✅ תחילת חודש: ' + d + ' לחודש'); return; }
    if (await confirmDialog({ title: 'תחילת חודש: ' + d, okLabel: 'עדכן',
      body: r.n + ' פעולות עו"ש שמורות בתקופה לפי היום הקודם. לשייך אותן מחדש לפי ' + d + ' לחודש? פעולות אשראי כבר עודכנו לבד: הן נשארות במחזור החיוב של הדוח.' })) r.apply();
  }
  return (
    <div className="ss">
      <InstallSetting />
      <div className="sr"><div className="slbl">מצב כהה</div><button className={'tog' + (UI.dark ? ' on' : '')} onClick={() => setDark(!UI.dark)} aria-label="מצב כהה" /></div>
      <div className="sr"><div><div className="slbl">תחילת חודש</div><div className="ssub">היום בחודש שממנו מתחילה תקופה</div></div>
        <div className="row"><input type="number" min="1" max="28" value={ms} onChange={(e) => setMs(e.target.value)} onBlur={saveMs} style={{ width: 64, textAlign: 'center' }} /><span className="note">לחודש</span></div></div>
    </div>
  );
}

function Account() {
  const email = localStorage.getItem('sb_email') || 'מחובר';
  const cfg = getConfig();
  async function out() { if (await confirmDialog({ title: 'התנתקות', body: 'להתנתק מהחשבון?', okLabel: 'התנתק', danger: true })) logout(); }
  async function disconnect() {
    if (!(await confirmDialog({ title: 'ניתוק מבסיס הנתונים', okLabel: 'נתק', danger: true, body: 'כתובת ה-Supabase והמפתח יימחקו מהמכשיר הזה. הנתונים בענן יישארו.' }))) return;
    clearConfig();
    logout();
  }
  return (
    <div className="ss">
      <div className="sr"><div className="slbl">👤 {email}</div><button className="ubtn ubtn-danger ubtn-sm" onClick={out}>התנתק</button></div>
      {cfg && !cfg.fromEnv && <button className="ubtn ubtn-outline w100 mt10" style={{ fontSize: '.86rem' }} onClick={disconnect}>ניתוק מבסיס הנתונים (החלפת פרויקט Supabase)</button>}
    </div>
  );
}

